import React, { Component } from "react";
import { Configuration, getDataTable } from "../Configuration";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { Modal, Spin } from "antd";
import "antd/dist/antd.css";

const COUNTRIES = ["UAE", "KSA"];
const ENVIRONMENTS = ["PRODUCTION", "STAGING"];

/* Portal registry. Adding a new portal to the credential UI (e.g. "MYNTRA",
   "AJIO") is a one-line change here plus a backend push handler, by design --
   the storage column and procs already accept any value. */
const PORTALS = ["NAMSHI", "SHOPIFY"];

// Default endpoints, pre-filled to match what the export page used to hardcode.
const DEFAULT_URL = {
  PRODUCTION: "https://marketplace.namshi.net",
  STAGING: "https://marketplace.namstg.net",
};

/* Every value below is concatenated into an EXEC string by getDataTable, and a
   pasted credential JSON is full of quotes and braces. Doubling single quotes
   is what keeps a stray apostrophe from terminating the literal and turning the
   rest of the payload into SQL. Applied to EVERY interpolated value, without
   exception. */
const q = (v) =>
  v === null || v === undefined ? "" : String(v).replace(/'/g, "''");

export default class frmNamshiCredentials extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loginId: Configuration.USER_ID,
      loading: false,

      brands: [],
      rows: [],

      // editor
      brandID: "",
      portal: "NAMSHI",
      country: "UAE",
      environment: "PRODUCTION",
      projectName: "",
      projectID: "",
      credToken: "",
      apiUrl: DEFAULT_URL.PRODUCTION,
      jsonText: null, // null = leave stored JSON untouched on update
      jsonFileName: "",
      editingCredID: null,
      tokenMask: "",        // what the listing shows, e.g. ••••••••0054
      hasStoredToken: false,
      canReveal: false,
    };
    this.fileRef = React.createRef();
  }

  async componentDidMount() {
    await this.loadBrands();
    await this.loadRows();
  }

  // ── Loads ─────────────────────────────────────────────────────────────────

  loadBrands = async () => {
    const dt = await getDataTable(
      "EXEC dbo.NamshiCred_GetBrands @loginID = " + this.state.loginId
    );
    this.setState({ brands: Array.isArray(dt) ? dt : [] });
  };

  loadRows = async () => {
    this.setState({ loading: true });
    const dt = await getDataTable(
      "EXEC dbo.NamshiCred_List @loginID = " + this.state.loginId + ", @brandID = 0"
    );
    this.setState({ rows: Array.isArray(dt) ? dt : [], loading: false });
  };

  // ── Editor helpers ────────────────────────────────────────────────────────

  onEnvChange = (env) => {
    // Only auto-fill the URL when the user hasn't typed their own.
    const isDefault =
      !this.state.apiUrl ||
      Object.values(DEFAULT_URL).indexOf(this.state.apiUrl) !== -1;
    this.setState({
      environment: env,
      apiUrl: isDefault ? DEFAULT_URL[env] : this.state.apiUrl,
    });
  };

  onPickJson = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target.result || "");
      try {
        JSON.parse(text); // reject a malformed file before it reaches the DB
      } catch (err) {
        Swal.fire("Invalid JSON", "That file is not valid JSON.", "error");
        if (this.fileRef.current) this.fileRef.current.value = "";
        return;
      }
      this.setState({ jsonText: text, jsonFileName: f.name });
    };
    reader.readAsText(f);
  };

  resetEditor = () => {
    this.setState({
      brandID: "",
      country: "UAE",
      environment: "PRODUCTION",
      projectName: "",
      projectID: "",
      credToken: "",
      apiUrl: DEFAULT_URL.PRODUCTION,
      jsonText: null,
      jsonFileName: "",
      editingCredID: null,
      tokenMask: "",
      hasStoredToken: false,
      canReveal: false,
    });
    if (this.fileRef.current) this.fileRef.current.value = "";
  };

  /* The listing deliberately carries only a mask, so editing starts with the
     token box EMPTY rather than pre-filled. Leaving it empty on save keeps the
     stored token; the user reveals it only if they actually need to see or
     replace it. That way simply editing a Project ID never pulls a credential
     into the page. */
  editRow = (r) => {
    this.setState({
      brandID: String(r.BrandID),
      country: r.Country,
      environment: r.Environment,
      projectName: r.ProjectName || "",
      projectID: r.ProjectID || "",
      credToken: "",
      tokenMask: r.CredTokenMask || "",
      hasStoredToken: !!r.hasToken,
      // Only a direct member of the owning org may decrypt. An agent acting
      // for the org can edit and post, but not read the stored secret.
      canReveal: !!r.canReveal,
      apiUrl: r.ApiUrl || DEFAULT_URL[r.Environment] || "",
      jsonText: null, // untouched unless a new file is chosen
      jsonFileName: r.JsonFileName || "",
      editingCredID: r.CredID,
      // Portal persists on edit, so hitting Update does not accidentally retype
      // a Namshi row as a Shopify one just because the dropdown defaulted.
      portal: r.Portal || "NAMSHI",
    });
    if (this.fileRef.current) this.fileRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* Decrypt and show the stored token. A separate, deliberate request — the
     value is never part of the browse response. */
  revealToken = async () => {
    if (!this.state.editingCredID) return;
    const dt = await getDataTable(
      "EXEC dbo.NamshiCred_RevealToken @loginID = " + this.state.loginId +
      ", @credID = " + this.state.editingCredID
    );
    const row = Array.isArray(dt) && dt.length ? dt[0] : null;
    if (!row || !row.CredToken) {
      Swal.fire({ icon: "info", title: "No token stored", timer: 1600, showConfirmButton: false });
      return;
    }
    this.setState({ credToken: row.CredToken });
  };

  // ── Save / delete ─────────────────────────────────────────────────────────

  save = async () => {
    const {
      loginId, brandID, country, environment,
      projectName, projectID, credToken, apiUrl, jsonText, jsonFileName,
    } = this.state;

    if (!brandID) {
      Swal.fire({ icon: "info", title: "Please select a Brand", timer: 1800, showConfirmButton: false });
      return;
    }
    // On a NEW credential a token is required. When editing, an empty box means
    // "keep the stored one" — the proc treats a blank token that way, so the
    // user never has to pull the secret into the page just to edit a Project ID.
    if ((!credToken || !credToken.trim()) && !this.state.hasStoredToken) {
      Swal.fire({ icon: "info", title: "Please enter the Credential Token", timer: 1800, showConfirmButton: false });
      return;
    }

    this.setState({ loading: true });

    // jsonText null -> omit the param so the proc keeps whatever is stored.
    const jsonPart =
      jsonText === null
        ? ""
        : ", @credJson = '" + q(jsonText) + "', @jsonFileName = '" + q(jsonFileName) + "'";

    const sql =
      "EXEC dbo.NamshiCred_Save" +
      " @loginID = " + loginId +
      ", @brandID = " + parseInt(brandID, 10) +
      ", @country = '" + q(country) + "'" +
      ", @environment = '" + q(environment) + "'" +
      ", @projectName = '" + q(projectName) + "'" +
      ", @projectID = '" + q(projectID) + "'" +
      ", @credToken = '" + q(credToken.trim()) + "'" +
      ", @apiUrl = '" + q(apiUrl.trim()) + "'" +
      ", @portal = '" + q(this.state.portal || "NAMSHI") + "'" +
      jsonPart;

    const res = await getDataTable(sql);
    this.setState({ loading: false });

    // The proc RAISERRORs when the brand is outside the user's organisation,
    // and getDataTable returns false rather than rows in that case.
    if (res === false) {
      Swal.fire(
        "Not saved",
        "You may not manage credentials for this brand, or the save failed.",
        "error"
      );
      return;
    }

    Swal.fire({
      icon: "success",
      title: this.state.editingCredID ? "Credential updated" : "Credential saved",
      timer: 1600,
      showConfirmButton: false,
    });
    this.resetEditor();
    await this.loadRows();
  };

  remove = async (r) => {
    const ok = await Swal.fire({
      title: "Delete this credential?",
      html:
        "<b>" + (r.BrandName || "") + "</b> — " + r.Country + " / " + r.Environment +
        "<br/><span style='font-size:12px;color:#888'>Posting for this brand and country will stop working until a new credential is saved.</span>",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;

    this.setState({ loading: true });
    await getDataTable(
      "EXEC dbo.NamshiCred_Delete @loginID = " + this.state.loginId + ", @credID = " + r.CredID
    );
    this.setState({ loading: false });
    await this.loadRows();
  };

  viewJson = async (r) => {
    const dt = await getDataTable(
      "EXEC dbo.NamshiCred_GetJson @loginID = " + this.state.loginId + ", @credID = " + r.CredID
    );
    const row = Array.isArray(dt) && dt.length ? dt[0] : null;
    if (!row || !row.CredJson) {
      Swal.fire({ icon: "info", title: "No JSON stored for this credential", timer: 1800, showConfirmButton: false });
      return;
    }
    let pretty = row.CredJson;
    try {
      pretty = JSON.stringify(JSON.parse(row.CredJson), null, 2);
    } catch (e) {
      /* show it raw if it will not re-parse */
    }
    Modal.info({
      title: row.JsonFileName || "Credential JSON",
      width: Math.min(720, window.innerWidth * 0.92),
      okText: "CLOSE",
      closable: true,
      maskClosable: true,
      style: { top: 70, padding: 0 },
      bodyStyle: { padding: "14px 18px" },
      content: (
        <pre style={{ fontSize: 12, lineHeight: 1.5, background: "#f6f8fa", padding: 12, borderRadius: 6, margin: 0 }}>
          {pretty}
        </pre>
      ),
    });
  };

  help = () => {
    const step = { display: "flex", gap: 9, marginBottom: 8, alignItems: "flex-start" };
    const num = {
      minWidth: 22, height: 22, borderRadius: "50%", background: "#3D9970",
      color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: 11, flexShrink: 0,
    };
    const txt = { fontSize: 12, color: "#333", lineHeight: 1.5 };
    const sec = { background: "#f5f5f5", borderRadius: 5, padding: "10px 14px", marginBottom: 10, borderLeft: "3px solid #3D9970" };
    const secT = { fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "#3D9970", marginBottom: 8 };
    const note = { background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 5, padding: "8px 12px", fontSize: 12, color: "#555" };

    Modal.info({
      title: "HOW TO USE — PORTAL CREDENTIALS",
      okText: "CLOSE",
      width: Math.min(660, window.innerWidth * 0.92),
      closable: true,
      okButtonProps: { style: { display: "none" } },
      footer: null,
      maskClosable: true,
      style: { top: 70, padding: 0 },
      bodyStyle: { padding: "14px 18px" },
      content: (
        <div>
          <div style={sec}>
            <div style={secT}>Saving a credential</div>
            <div style={step}><div style={num}>1</div><div style={txt}><strong>Select Brand</strong> — only brands belonging to your organisation are listed.</div></div>
            <div style={step}><div style={num}>2</div><div style={txt}><strong>Country</strong> — UAE or KSA. Each country needs its own credential.</div></div>
            <div style={step}><div style={num}>3</div><div style={txt}><strong>Environment</strong> — PRODUCTION pushes to the live store, STAGING to the test store. The API URL fills in automatically.</div></div>
            <div style={step}><div style={num}>4</div><div style={txt}><strong>Project Name / Project ID</strong> — as given by Namshi, e.g. <em>Steve Madden_UAE</em> and <em>PRJ460518</em>.</div></div>
            <div style={step}><div style={num}>5</div><div style={txt}><strong>Credential Token</strong> — the value Namshi issues for this project. Required; this is what is sent with every push.</div></div>
            <div style={step}><div style={num}>6</div><div style={txt}><strong>JSON File</strong> — optional. Attach the credential file supplied by Namshi; it is validated and stored against this brand.</div></div>
            <div style={step}><div style={num}>7</div><div style={txt}><strong>Save</strong> — a brand/country/environment already saved is updated in place.</div></div>
          </div>
          <div style={sec}>
            <div style={secT}>How it is used</div>
            <div style={txt}>
              On <strong>Export Data</strong>, choosing <strong>POST API LIVE → FOR UAE</strong> or <strong>FOR KSA</strong> looks up the credential
              saved here for that brand and country. If none is saved, the push is stopped with a message rather than
              falling back to another organisation's account.
            </div>
          </div>
          <div style={note}>
            <strong>Access:</strong> you can only see and edit brands in your own organisation, or one you are mapped to as an agent.
            This is enforced in the database, not just in this screen — a credential belonging to another organisation
            cannot be read, saved or used for posting.
          </div>
        </div>
      ),
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  render() {
    const {
      loading, brands, rows, brandID, country, environment,
      projectName, projectID, credToken, apiUrl, jsonFileName, editingCredID,
    } = this.state;

    const lbl = { display: "block", marginBottom: 3 };

    return (
      <div className="content-wrapper">
        {loading && (
          <div className="loader" style={{ display: "block" }}>
            <div className="loader-item"><Spin /></div>
          </div>
        )}

        <section className="content">
          <div className="box box-success">
            <div className="box-header with-border" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 20px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "#aaa", marginBottom: 3 }}>
                  Dashboard &gt; Configuration &gt; Portal Credentials
                </div>
                <h3 className="box-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  Portal Credentials
                </h3>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
                <button className="btn btn-sm btn-default" onClick={this.help}>Help</button>
              </div>
            </div>

            <div className="box-body">
              {/* ── Editor ─────────────────────────────────────────── */}
              <div className="page-filter-card" style={{ background: "#f8f9fa", border: "1px solid #e8e8e8", borderRadius: 8, padding: "16px 20px", marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#3D9970", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>1</div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    {editingCredID ? "Update Credential" : "Add Credential"}
                  </div>
                  {editingCredID && (
                    <button className="btn btn-xs btn-default" style={{ marginLeft: 8 }} onClick={this.resetEditor}>
                      Cancel edit
                    </button>
                  )}
                </div>

                {/* First row: the four classification fields, equal widths at
                    desktop (3 cols each = 12). Prevents the old spill where
                    adding Portal pushed Project ID to its own row. */}
                <div className="row">
                  <div className="col-xs-6 col-sm-3 col-md-3 margintop">
                    <label style={lbl}>Portal <span className="vcode">*</span></label>
                    <select className="form-control" value={this.state.portal}
                      onChange={(e) => {
                        const p = e.target.value;
                        this.setState({
                          portal: p,
                          apiUrl: p === "NAMSHI"
                            ? DEFAULT_URL[this.state.environment] || ""
                            : "",
                        });
                      }}>
                      {PORTALS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>

                  <div className="col-xs-12 col-sm-6 col-md-3 margintop">
                    <label style={lbl}>Brand <span className="vcode">*</span></label>
                    <select className="form-control" value={brandID}
                      onChange={(e) => this.setState({ brandID: e.target.value })}>
                      <option value="">Please Select Brand</option>
                      {brands.map((b) => (
                        <option key={b.DisplayId} value={b.DisplayId}>
                          {b.DisplayName}{b.OrgName ? " — " + b.OrgName : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-xs-6 col-sm-3 col-md-3 margintop">
                    <label style={lbl}>Country <span className="vcode">*</span></label>
                    {this.state.portal === "NAMSHI" ? (
                      <select className="form-control" value={country}
                        onChange={(e) => this.setState({ country: e.target.value })}>
                        {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <input className="form-control" value={country} placeholder="AE, SA, UK, …"
                        onChange={(e) => this.setState({ country: e.target.value.toUpperCase() })} />
                    )}
                  </div>

                  <div className="col-xs-6 col-sm-3 col-md-3 margintop">
                    <label style={lbl}>Environment <span className="vcode">*</span></label>
                    <select className="form-control" value={environment}
                      onChange={(e) => this.onEnvChange(e.target.value)}>
                      {ENVIRONMENTS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {/* Second row: project naming, side by side. */}
                <div className="row">
                  <div className="col-xs-12 col-sm-6 col-md-6 margintop">
                    <label style={lbl}>Project Name</label>
                    <input className="form-control" value={projectName} placeholder="Steve Madden_UAE"
                      onChange={(e) => this.setState({ projectName: e.target.value })} />
                  </div>

                  <div className="col-xs-12 col-sm-6 col-md-6 margintop">
                    <label style={lbl}>Project ID</label>
                    <input className="form-control" value={projectID} placeholder="PRJ460518"
                      onChange={(e) => this.setState({ projectID: e.target.value })} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-xs-12 col-sm-6 col-md-4 margintop">
                    <label style={lbl}>
                      Credential Token {!this.state.hasStoredToken && <span className="vcode">*</span>}
                    </label>
                    <div style={{ display: "flex", gap: 6 }}>
                      <input className="form-control" value={credToken} style={{ flex: 1 }}
                        placeholder={
                          this.state.hasStoredToken
                            ? "Stored (" + (this.state.tokenMask || "hidden") + ") — leave blank to keep"
                            : (this.state.portal === "SHOPIFY"
                                ? "Shopify Admin API access token (shpat_…)"
                                : "Namshi cred value")
                        }
                        onChange={(e) => this.setState({ credToken: e.target.value })} />
                      {this.state.hasStoredToken && this.state.canReveal && (
                        <button className="btn btn-default" style={{ whiteSpace: "nowrap" }}
                          onClick={this.revealToken} title="Decrypt and show the stored token">
                          Reveal
                        </button>
                      )}
                    </div>
                    {this.state.hasStoredToken && (
                      <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>
                        {this.state.canReveal
                          ? "Stored encrypted. Leave blank to keep it unchanged."
                          : "Stored encrypted. You act for this organisation but are not a member, so it cannot be shown — type a new value to replace it."}
                      </div>
                    )}
                  </div>

                  <div className={"margintop " + (this.state.portal === "SHOPIFY" ? "col-xs-12 col-sm-6 col-md-8" : "col-xs-12 col-sm-6 col-md-4")}>
                    {/* Namshi takes a fixed marketplace URL; Shopify takes the
                        merchant's own store domain, which doubles as the shop
                        identifier the /shopify/postData endpoint needs. */}
                    <label style={lbl}>{this.state.portal === "SHOPIFY" ? "Shop Domain" : "API URL"}</label>
                    <input className="form-control" value={apiUrl}
                      placeholder={this.state.portal === "SHOPIFY" ? "my-store.myshopify.com" : ""}
                      onChange={(e) => this.setState({ apiUrl: e.target.value })} />
                  </div>

                  {/* The JSON file is a Namshi-only concept (a service-account
                      style credential some projects use alongside the token).
                      Shopify auth is the single access token above, so this
                      column is hidden entirely for a Shopify row rather than
                      left showing a field that is never read for that portal. */}
                  {this.state.portal !== "SHOPIFY" && (
                    <div className="col-xs-12 col-sm-6 col-md-4 margintop">
                      <label style={lbl}>Credential JSON File</label>
                      <input type="file" accept=".json,application/json"
                        ref={this.fileRef} className="form-control"
                        style={{ paddingTop: 4 }} onChange={this.onPickJson} />
                      {jsonFileName ? (
                        <div style={{ fontSize: 11, color: "#3D9970", marginTop: 3 }}>
                          {this.state.jsonText === null ? "Stored file: " : "New file: "}{jsonFileName}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                  <button className="btn btn-success" onClick={this.save}>
                    {editingCredID ? "Update Credential" : "Save Credential"}
                  </button>
                </div>
              </div>

              {/* ── Saved list ─────────────────────────────────────── */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 14px" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#3D9970", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>2</div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Saved Credentials</div>
              </div>

              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>SR NO.</th>
                      <th>PORTAL</th>
                      <th>BRAND</th>
                      <th>ORGANISATION</th>
                      <th>COUNTRY</th>
                      <th>ENVIRONMENT</th>
                      <th>PROJECT NAME</th>
                      <th>PROJECT ID</th>
                      <th>TOKEN</th>
                      <th>JSON</th>
                      <th>LAST UPDATED</th>
                      <th style={{ width: 130 }}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan="12" style={{ textAlign: "center", color: "#999", padding: 18 }}>
                          No credentials saved yet for your organisation.
                        </td>
                      </tr>
                    )}
                    {rows.map((r, i) => (
                      <tr key={r.CredID}>
                        <td>{i + 1}</td>
                        <td>
                          <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                            background: r.Portal === "SHOPIFY" ? "#f0f5ff" : "#fff7e6",
                            color:      r.Portal === "SHOPIFY" ? "#2f54eb" : "#d46b08" }}>
                            {r.Portal || "NAMSHI"}
                          </span>
                        </td>
                        <td><strong>{r.BrandName}</strong></td>
                        <td style={{ fontSize: 12, color: "#666" }}>{r.OrgName}</td>
                        <td>
                          <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, background: r.Country === "UAE" ? "#e6f7ff" : "#f6ffed", color: r.Country === "UAE" ? "#1890ff" : "#389e0d" }}>
                            {r.Country}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: 11, fontWeight: 700, color: r.Environment === "PRODUCTION" ? "#cf1322" : "#888" }}>
                            {r.Environment}
                          </span>
                        </td>
                        <td>{r.ProjectName}</td>
                        <td>{r.ProjectID}</td>
                        <td style={{ fontFamily: "monospace", fontSize: 11, color: r.hasToken ? "#389e0d" : "#bbb" }}>
                          {r.hasToken ? r.CredTokenMask : "—"}
                        </td>
                        <td>
                          {!r.hasJson ? (
                            <span style={{ color: "#bbb", fontSize: 11 }}>—</span>
                          ) : r.canReveal ? (
                            <button className="btn btn-xs btn-default" onClick={() => this.viewJson(r)}>View</button>
                          ) : (
                            <span style={{ fontSize: 11, color: "#389e0d", fontWeight: 600 }}
                                  title="Stored. Only a member of the owning organisation can view it.">
                              Stored
                            </span>
                          )}
                        </td>
                        <td style={{ fontSize: 11, color: "#888" }}>
                          {r.UpdatedOn ? String(r.UpdatedOn).substring(0, 10) : ""}
                          {r.UpdatedByName ? " · " + r.UpdatedByName : ""}
                        </td>
                        <td>
                          <button className="btn btn-xs btn-default" style={{ marginRight: 4 }} onClick={() => this.editRow(r)}>Edit</button>
                          <button className="btn btn-xs btn-danger" onClick={() => this.remove(r)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
}
